import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Row, Container, Form, Spinner, UncontrolledAccordion } from "reactstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import APP_ROUTES from "src/routes/routes";
import { FORM_NAME, FORM_TYPE } from "src/constant/common";
import { get, isEmpty } from "lodash";
import MainPart from "../components/MainPart";
import VehiclePart from "../components/VehiclePart";
import EnvironmentPart from "../components/EnvironmentPart";
import InitialConditionPart from "../components/InitialConditionPart";
import TargetPart from "../components/TargetPart";
import { dataJson } from "src/recoil/atoms/global";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { navigationPromptState } from "src/recoil/atoms/global";
import MCSSigma from "../components/MCSSigma";
import MCSMission from "../components/MCSMission";
import { extractFormValuesForSimMCS } from "src/utils/common";
import { CustomLink } from "src/components/Common/CustomLink";
import { usePatchedNavigate } from "src/Hooks/usePatchedNavigate";

const FormGNCSimulator6DOF = ({
  submitData,
  data,
  title,
  getRedirectURL = () => APP_ROUTES.GNC_SIMULATOR_6DOF,
  versions,
  isUploadingJsonFile,
  handleRedirectURLSimParent,
}) => {
  const { t } = useTranslation("gnc-simulator-6dof");
  const navigate = usePatchedNavigate();
  const [isSubmit, setIsSubmit] = useState(false);
  const [isUpload, setIsUpload] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isSimModeMCS, setIsSimModeMCS] = useState(false);
  let [searchParams] = useSearchParams();
  const formAction = searchParams.get("action");
  const isEditOrCopy = formAction === FORM_TYPE.COPY || formAction === FORM_TYPE.EDIT;
  const jsonValue = useRecoilValue(dataJson);
  const navigationPrompt = useSetRecoilState(navigationPromptState);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    getValues,
    setValue,
    clearErrors,
  } = useForm({
    defaultValues: data,
  });

  const { fields, update } = useFieldArray({
    control,
    name: "series",
  });

  const checkAndClearErrors = (fieldValue, formName, clearErrors) => {
    if (fieldValue || fieldValue === 0) {
      clearErrors(formName);
    }
  };

  const updateFormValues = (simulationMode, shouldProcessJsonValue = false) => {
    let formSections = [
      { section: "main", fields: FORM_NAME.MAIN },
      { section: "vehicle", fields: FORM_NAME.VEHICLE },
      { section: "environment", fields: FORM_NAME.ENVIRONMENT },
      { section: "initial_condition", fields: FORM_NAME.INITIAL_CONDITION },
      { section: "target", fields: FORM_NAME.TARGET },
    ];

    if (simulationMode === 1) {
      formSections.push({ section: "MCS", fields: FORM_NAME.MCS });

      if (!shouldProcessJsonValue) {
        extractFormValuesForSimMCS("sigma_I_s", data?.MCS, ["x", "y", "z"], data);
        extractFormValuesForSimMCS("sigma_I_e", data?.MCS, ["x", "y", "z"], data);
      }
    } else {
      formSections = formSections.filter((section) => section.section !== "MCS");
    }

    formSections.forEach(({ section, fields }) => {
      Object.keys(fields).forEach((key) => {
        const fieldKey = fields[key];
        const formName = `${section}.${fieldKey}`;
        const fieldValue = data?.[section]?.[fieldKey];

        setValue(formName, fieldValue);
        checkAndClearErrors(fieldValue, formName, clearErrors);
      });
    });
  };

  useEffect(() => {
    if (!isUploadingJsonFile && !isEmpty(jsonValue)) {
      const simulationMode = getValues(`${FORM_NAME.TYPE}`);
      updateFormValues(simulationMode, true);
    }
  }, [isUploadingJsonFile, jsonValue]);

  useEffect(() => {
    const simulationMode = getValues(`${FORM_NAME.TYPE}`);
    updateFormValues(simulationMode);
    setIsSimModeMCS(simulationMode === 1);
  }, [getValues(`${FORM_NAME.TYPE}`)]);

  const handleSubmitForm = (data) => {
    navigationPrompt(false);
    setIsSubmit(true);
    const formData = {
      ...data,
      processAsRoot: isChecked,
    };
    return submitData({ data: formData }, (isSubmitFailed) => {
      setIsSubmit(false);
      isSubmitFailed && isDirty && navigationPrompt(true);
    });
  };

  const handleUpdateAttachments = (attachments, index, fieldName) => {
    update(index, {
      ...fields[index],
      [fieldName]: attachments?.map((i) => i),
    });

    clearErrors(`series.${index}.${fieldName}`);
  };

  const handleError = (errors) => {
    const errorFieldNames = Object.keys(errors);
    if (errorFieldNames.length === 0) return;

    const accordionHeaders = document.querySelectorAll(".accordion-button");
    let isFirstErrorFocused = false;

    accordionHeaders.forEach((header) => {
      const accordionItem = header.closest(".accordion-item");
      const inputElements = accordionItem.querySelectorAll("input, select, textarea, .form-group");

      const hasErrorInItem = Array.from(inputElements).some((input) => {
        const inputName = input.getAttribute("name") || input.dataset.name;
        return get(errors, inputName);
      });

      if (hasErrorInItem && header.classList.contains("collapsed")) {
        setTimeout(() => header.click(), 100);
      }

      if (hasErrorInItem && !isFirstErrorFocused) {
        const firstErrorInput = Array.from(inputElements).find((input) => {
          const inputName = input.getAttribute("name") || input.dataset.name;
          return get(errors, inputName);
        });

        if (!firstErrorInput) return;

        setTimeout(() => {
          firstErrorInput.scrollIntoView({ behavior: "smooth", block: "center" });
          if (["INPUT", "TEXTAREA", "SELECT", "DIV"].includes(firstErrorInput.tagName)) {
            firstErrorInput.focus();
          }
        }, 650);

        isFirstErrorFocused = true;
      }
    });
  };

  useEffect(() => {
    if (isDirty) {
      navigationPrompt(true);
    }
  }, [isDirty]);

  useEffect(() => {
    const simulationMode = getValues(`${FORM_NAME.TYPE}`);
    setIsSimModeMCS(simulationMode === 1);
  }, [getValues(`${FORM_NAME.TYPE}`)]);
  return (
    <Container fluid className="gnc-simulator-6dof-container">
      <Breadcrumb>
        <BreadcrumbItem>
          <CustomLink to={getRedirectURL(data?.page)} className="link-primary">
            {t("list.title_breadcrumb")}
          </CustomLink>
        </BreadcrumbItem>
        <BreadcrumbItem active>{isEditOrCopy ? t("new.title_breadcrumb_detail") : t("new.title_breadcrumb_new")}</BreadcrumbItem>
      </Breadcrumb>

      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="header-title">{title || t("new.title")}</div>
              <Form onSubmit={handleSubmit(handleSubmitForm, handleError)} className="simulator-6dof-form">
                <UncontrolledAccordion defaultOpen={["1"]} stayOpen>
                  <MainPart
                    register={register}
                    errors={errors}
                    control={control}
                    fields={fields}
                    isChecked={isChecked}
                    setIsChecked={setIsChecked}
                    setIsUpload={setIsUpload}
                    versions={versions}
                    simData={data}
                    handleUpdateAttachments={handleUpdateAttachments}
                    formType={formAction}
                    handleRedirectURLSimParent={handleRedirectURLSimParent}
                    isSimModeMCS={isSimModeMCS}
                    setIsSimModeMCS={setIsSimModeMCS}
                  />
                  <VehiclePart register={register} errors={errors} />
                  <EnvironmentPart register={register} errors={errors} />
                  <InitialConditionPart register={register} errors={errors} getValues={getValues} setValue={setValue} />
                  <TargetPart register={register} errors={errors} />
                  {isSimModeMCS && (
                    <>
                      <MCSSigma register={register} errors={errors} />
                      <MCSMission register={register} errors={errors} />
                    </>
                  )}
                </UncontrolledAccordion>

                <div className="d-flex justify-content-center gap-2 mt-2">
                  <span
                    role="button"
                    className="btn btn-secondary width-by-px-200 height-by-px-38"
                    onClick={() => navigate(getRedirectURL(data?.page))}
                  >
                    <span>{t("new.cancel")}</span>
                  </span>
                  <button className="btn btn-primary width-by-px-200 height-by-px-38" disabled={isSubmit || isUpload} type="submit">
                    {isSubmit ? <Spinner size="sm" /> : <span>{formAction === FORM_TYPE.EDIT ? t("new.retry") : t("new.submit")}</span>}
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default FormGNCSimulator6DOF;
